import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikState } from 'formik';
import { Form, Header } from 'semantic';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import DateField from 'components/form-fields/formik/DateField';
import Divider from 'components/Sidebar/Divider';
import { TariffFormFields } from '../../Tariff/TariffFormFields';
import { TariffRule as CustomTariffRuleType } from '../../types';
import { buildFormPath } from 'subdomains/cpo/screens/GroupsAndPolicies/formpath';
import AuthorizationMethodSelection from './AuthorizationMethodSelection';
import CustomerGroupSelection from './CustomerGroupSelection';

type Props = {
  values: FormikState<CustomTariffRuleType>['values'];
  setFieldValue: (field: string, value: any) => void;
  basePath?: string;
};

export const TariffRuleFormFields: React.FC<Props> = ({
  values,
  setFieldValue,
  basePath,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Header
        as="h3"
        content={t('chargingPolicy.pricing.tariffTitle', 'Tariff')}
      />
      <TariffFormFields
        basePath={buildFormPath('tariff', basePath)}
        values={values?.tariff}
        setFieldValue={setFieldValue}
      />
      <Header
        as="h4"
        content={t('chargingPolicy.pricing.tariffOptions', 'Options')}
      />
      <CheckboxField
        toggle
        name={buildFormPath('validityPeriod.enabled', basePath)}
        label={t(
          'chargingPolicy.pricing.validityPeriodEnabled',
          'This tariff has a validity period'
        )}
      />
      {values?.validityPeriod?.enabled && (
        <>
          <p
            style={{
              fontSize: '0.85rem',
              marginLeft: '4.6rem',
              marginTop: '-0.8rem',
            }}>
            {t(
              'chargingPolicy.pricing.validityPeriod.description',
              'Ensure that your tariff components with date restrictions are within this tariff validity period.'
            )}
          </p>
          <>
            <Divider />
            <Form.Group>
              <DateField
                name={buildFormPath('validityPeriod.start', basePath)}
                label={t(
                  'chargingPolicy.pricing.restrictions.startDate',
                  'Start date'
                )}
              />
              <DateField
                name={buildFormPath('validityPeriod.end', basePath)}
                label={t(
                  'chargingPolicy.pricing.restrictions.endDate',
                  'End date'
                )}
              />
            </Form.Group>
          </>
        </>
      )}

      <Header
        as="h4"
        content={t(
          'chargingPolicy.pricing.paymentOptionsTitle',
          'Payment options this tariff applies to'
        )}
      />
      <AuthorizationMethodSelection
        values={values?.authorizationMethods}
        onChange={(values) =>
          setFieldValue(buildFormPath('authorizationMethods', basePath), values)
        }
      />

      <Header
        as="h4"
        content={t(
          'chargingPolicy.pricing.customerGroupsTitle',
          'Who does this tariff apply to'
        )}
      />
      <CustomerGroupSelection
        values={values?.customerGroups}
        onChange={(values) =>
          setFieldValue(buildFormPath('customerGroups', basePath), values)
        }
      />
    </>
  );
};
